import Button from '@components/Button';
import Checkbox from '@components/Checkbox';
import { showConfirmModal } from '@components/ConfirmModal';
import { sendSuccess } from '@components/Noti';
import Tip from '@components/Tip';
import { TipWrapper } from '@components/TipWrapper';
import Title from '@components/Title';
import IconExit from '@components/icons/IconExit';
import { customButtonDefault } from '@modules/edit';
import { $sceneMapApi } from '@stores/dataStores';
import { $editingHotspotApi } from '@stores/editingHotspotStores';
import { $GateHotspotBatchMode } from '@stores/hotspotStores';
import { MainPlayer } from '@stores/krpPlayerStores';
import { FeatureType, HSIconType, type Hotspot, type SceneData } from '@types';
import getSelectedStatus from '@utils/getSelectedStatus';
import { useSelect } from 'downshift';
import { useGate } from 'effector-react';
import { nanoid } from 'nanoid';
import { toFlag } from 'pano-utils';
import { useCallback, useEffect, useState } from 'react';
import { Scrollbars } from 'react-custom-scrollbars';
import { useImmer } from 'use-immer';
import { showSceneModal } from '../../category';
import SidebarHeader from '../../sidebar/components/SidebarHeader';
import { deleteHotspots } from '../actions/deleteHotspots';
import {
  HOTSPOT_MAX,
  TITLE_SETTING_DEFAULT,
  getHotspotTitleId,
  hotspotIconDefault,
  krpRemoveHotspot,
  onHotspotClick,
  toggleHotspotSelected,
} from '../index';
import BatchModeItem from './BatchModeItem';
import HotspotBatchSetting from './HotspotBatchSetting/HotspotBatchSetting';

const Style = '修改热点样式';
const Hide = '隐藏热点';
const Display = '显示热点';
const Lock = '锁定热点';
const Unlock = '解锁热点';
const Copy = '热点复制到...';
const Delete = '删除热点';
interface Props {
  scene: SceneData;
  onQuitClick: () => void;
}

export function HotspotBatchModeList({ scene, onQuitClick }: Props) {
  const hotspotList = scene.hotspot ?? [];
  const [selected, setSelected] = useImmer<Set<string>>(new Set());
  const [showBatchSetting, setShowBatchSetting] = useState(false);

  const items = [Style, Hide, Display, Lock, Unlock, Copy, Delete];

  const { isOpen, getToggleButtonProps, getMenuProps, getItemProps } =
    useSelect({
      items,
      selectedItem: null,
      onSelectedItemChange: (changes) => {
        switch (changes.selectedItem) {
          case Copy:
            handleCopyTo();
            break;
          case Delete:
            handleBatchDelete();
            break;
          case Hide:
            handleToggleVisible(false);
            break;
          case Display:
            handleToggleVisible(true);
            break;
          case Lock:
            handleToggleLock(true);
            break;
          case Unlock:
            handleToggleLock(false);

            break;
          case Style: {
            const newHotspot: Hotspot = {
              id: 'batchSetting',
              zOrder: 0,
              icon: hotspotIconDefault({
                type: HSIconType.system,
                ath: 0,
                atv: -90,
              }),
              title: '',
              showTitle: 1,
              titleSetting: TITLE_SETTING_DEFAULT,
              data: {
                ...customButtonDefault(FeatureType.noFunc),
              },
              visible: 1,
              locked: 0,
            };
            $editingHotspotApi.set({
              hotspot: newHotspot,
              sceneId: scene.id,
              isNew: true,
              isBatch: true,
              batchHotspots: hotspotList.filter((x) => selected.has(x.id)),
              sceneType: scene.type,
            });
            setShowBatchSetting(true);
            break;
          }
          default:
            break;
        }
      },
    });
  useGate($GateHotspotBatchMode, true);

  useEffect(() => {
    const unwatch = onHotspotClick.watch(({ id }) => {
      if (!showBatchSetting) {
        toggleSelected(id);
      }
    });
    return () => {
      unwatch();
    };
  }, [showBatchSetting]);

  useEffect(() => {
    hotspotList.forEach((hotspot) => {
      toggleHotspotSelected({
        id: hotspot.id,
        isOn: selected.has(hotspot.id),
      });
    });
  }, [selected]);

  useEffect(() => {
    return () => {
      hotspotList.forEach((hotspot) => {
        toggleHotspotSelected({
          id: hotspot.id,
          isOn: false,
        });
      });
    };
  }, []);

  const toggleSelected = (id: string) => {
    setSelected((draft) => {
      if (draft.has(id)) {
        draft.delete(id);
      } else {
        draft.add(id);
      }
    });
  };

  const handleBatchItemClick = useCallback((params: { id: string }) => {
    toggleSelected(params.id);
    MainPlayer.get()?.lookToHotspot({ id: params.id });
  }, []);

  const handleSelectAll = () => {
    if (selected.size < hotspotList.length) {
      setSelected(new Set(hotspotList.map((x) => x.id)));
    } else {
      setSelected((draft) => {
        draft.clear();
      });
    }
  };

  const handleBatchDelete = async () => {
    const { confirmed } = await showConfirmModal({
      title: '操作确认',
      content: (
        <>
          <div className="mb-5 text-xl">{`确认删除选中的${selected.size}个热点吗？`}</div>
          <div className="text-center">删除后，热点不能被恢复。</div>
        </>
      ),
    });
    if (!confirmed) return;
    deleteHotspots({
      sceneId: scene.id,
      hotspotIds: selected,
    });
    selected.forEach((id) => {
      krpRemoveHotspot(id);
    });
    setSelected((draft) => {
      draft.clear();
    });
  };

  const handleToggleVisible = (value: boolean) => {
    $sceneMapApi.updateScene({
      id: scene.id,
      updater: (draft) => {
        draft.hotspot?.forEach((hotspot) => {
          if (selected.has(hotspot.id)) {
            hotspot.visible = toFlag(value);
          }
        });
      },
    });
    selected.forEach((id) => {
      MainPlayer.get()?.updateHotspot({
        id,
        visible: value,
        enabled: value,
      });
      if (hotspotList.filter((x) => x.id === id)[0].showTitle) {
        const titleId = getHotspotTitleId(id);
        MainPlayer.get()?.updateHotspot({
          id: titleId,
          visible: value,
          enabled: value,
        });
      }
    });
    sendSuccess(value ? '所选热点已显示' : '所选热点已隐藏');
  };

  const handleToggleLock = (value: boolean) => {
    $sceneMapApi.updateScene({
      id: scene.id,
      updater: (draft) => {
        draft.hotspot?.forEach((hotspot) => {
          if (selected.has(hotspot.id)) {
            hotspot.locked = toFlag(value);
          }
        });
      },
    });
    selected.forEach((id) => {
      MainPlayer.get()?.updateHotspot({
        id,
        dragable: !value,
      });
    });
    sendSuccess(value ? '所选热点已锁定' : '所选热点已解锁');
  };

  const handleCopyTo = async () => {
    const { idSet } = await showSceneModal({
      title: '选择要复制到的场景',
      subTitle: '注：只能复制到同类型场景中（不包括当前场景）',
      allowedTypes: [scene.type],
      disabledIds: [scene.id],
      multiple: true,
    });
    if (!idSet) return;
    const list = hotspotList.filter((x) => selected.has(x.id));
    $sceneMapApi.update((draft) => {
      Array.from(idSet).forEach((_id) => {
        const newList = list.map((x) => ({
          ...x,
          id: `h_${nanoid()}`,
          extendAction: undefined,
        }));
        const newHotspot = [...(draft.get(_id)!.hotspot ?? []), ...newList];
        if (newHotspot.length > HOTSPOT_MAX) {
          newHotspot.length = HOTSPOT_MAX;
        }
        newHotspot.forEach((x, i) => {
          x.zOrder = i;
        });
        draft.get(_id)!.hotspot = newHotspot;
      });
    });
    sendSuccess('热点已复制到所选场景');
  };

  return (
    <>
      <SidebarHeader
        title={
          <div className="flex">
            <Title>热点</Title>
            <Tip className="ml-10 self-end text-base">批量操作</Tip>
          </div>
        }
        btns={
          <TipWrapper text="退出操作" placement="bottom" size="sm">
            <IconExit className="focus:outline-none" onClick={onQuitClick} />
          </TipWrapper>
        }
      />
      <div
        className="mb-10 flex h-35 cursor-pointer items-center rounded bg-black-#34 px-10"
        onClick={handleSelectAll}
      >
        <Checkbox
          status={getSelectedStatus(selected.size, hotspotList.length)}
          onClick={handleSelectAll}
        />
        <div className="ml-5">全选</div>
        <div className="ml-auto text-sm text-black-gray ">{`${selected.size}/${hotspotList.length}`}</div>
      </div>
      {hotspotList.length > 0 ? (
        <div className="relative flex-1">
          <Scrollbars>
            {hotspotList.map((x) => (
              <BatchModeItem
                key={x.id}
                data={x}
                isSelected={selected.has(x.id)}
                onClick={handleBatchItemClick}
              />
            ))}
          </Scrollbars>
        </div>
      ) : (
        <Tip className="mt-5 flex-1 text-center text-base">
          没有热点可批量操作
        </Tip>
      )}

      <div className="relative">
        <div
          className="absolute z-30 w-full select-none overflow-hidden rounded bg-white text-black focus:outline-none"
          style={{ marginTop: `-${items.length * 30}px` }}
          {...getMenuProps()}
        >
          {isOpen &&
            items.map((item, index) => (
              <div
                className="flex h-30 cursor-pointer items-center justify-center px-10 hover:bg-white-light"
                key={item}
                {...getItemProps({ item, index })}
              >
                {item}
              </div>
            ))}
        </div>
        <Button
          className="w-full"
          disabled={!selected.size}
          {...getToggleButtonProps()}
          onClick={(event) => {
            getToggleButtonProps().onClick(event);
          }}
        >
          进行批量操作
        </Button>
      </div>
      <Button scheme="darkest" className="mt-10 w-full" onClick={onQuitClick}>
        退出
      </Button>
      {showBatchSetting && (
        <HotspotBatchSetting
          hotspotList={hotspotList}
          selectedHotspotIds={selected}
          onClose={(selectedHotspotIds) => {
            setSelected(selectedHotspotIds);
            setShowBatchSetting(false);
          }}
        />
      )}
    </>
  );
}
